/**
 * DebiteurenbeheerSection — Enterprise IA-refactor · Feb 2026.
 *
 * Deze module vervangt de losse "Debiteurenbeheer + Betalingsregelingen"
 * items met één samenhangende workspace voor de credit-controller /
 * financieel medewerker.
 *
 * Tab-structuur (per user-goedkeuring d.d. Feb 2026):
 *   1. Dashboard              — klant-werkwachtrij (voormalige DebiteurenDashboard)
 *   2. Facturen-opvolging     — per-factuur (voormalige BetalingsopvolgingTab uit Facturen)
 *   3. Betalingsregelingen    — voormalige aparte hoofdmodule
 *   4. Communicatie           — Communicatiecentrum gefilterd op debiteuren
 *   5. Stripe                 — Stripe events + retry queue
 *   6. Tijdlijn               — chronologische feed van alle debiteuren-activiteit
 *
 * GEEN functionaliteit verwijderd — alleen samengevoegd voor 1-klik workflow.
 * Bestaande deep-links (`?section=debiteuren`, `?section=regelingen`) blijven
 * werken via AdminPage.jsx redirect-logica.
 */
import React, { useState } from "react";
import { AdminPageHeader } from "../ui";
import { Users, ClipboardCheck, MessageSquare, CreditCard, Clock, ListTree } from "lucide-react";
import DebiteurenDashboard from "./DebiteurenDashboard";
import BetalingsopvolgingTab from "./BetalingsopvolgingTab";
import BetalingsregelingenSection from "./BetalingsregelingenSection";
import CommunicatieSection from "./CommunicatieSection";
import StripeEventsPanel from "./StripeEventsPanel";

const TABS = [
  { key: "dashboard",     label: "Dashboard",              icon: Users },
  { key: "opvolging",     label: "Facturen-opvolging",     icon: ClipboardCheck },
  { key: "regelingen",    label: "Betalingsregelingen",    icon: ListTree },
  { key: "communicatie",  label: "Communicatie",           icon: MessageSquare },
  { key: "stripe",        label: "Stripe",                 icon: CreditCard },
  { key: "tijdlijn",      label: "Tijdlijn",               icon: Clock },
];

export default function DebiteurenbeheerSection({ initialTab = "dashboard", onOpenComposer } = {}) {
  const [tab, setTab] = useState(initialTab);

  return (
    <div className="space-y-5" data-testid="debiteurenbeheer-section">
      <AdminPageHeader
        icon={Users}
        title="Debiteurenbeheer"
        subtitle="Alle operationele opvolging op één plek — dashboard, factuur-follow-up, regelingen, communicatie en Stripe."
      />

      {/* Tab bar */}
      <div className="flex gap-1 border border-gray-200 bg-white w-fit overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`debiteuren-tab-${t.key}`}
              className={`px-4 py-2 text-xs uppercase font-bold tracking-wide flex items-center gap-1.5 transition-colors ${
                isActive ? "bg-[#082c50] text-white" : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div data-testid={`debiteuren-tab-body-${tab}`}>
        {tab === "dashboard"    && <DebiteurenDashboard onOpenComposer={onOpenComposer} />}
        {tab === "opvolging"    && <BetalingsopvolgingTab />}
        {tab === "regelingen"   && <BetalingsregelingenSection />}
        {tab === "communicatie" && <CommunicatieSection />}
        {tab === "stripe"       && <StripeEventsPanel />}
        {tab === "tijdlijn"     && <DebiteurenTijdlijnPlaceholder />}
      </div>
    </div>
  );
}

/**
 * Tijdlijn — chronologische feed van ALLE debiteuren-activiteit.
 * Placeholder voor de volgende sprint; de data is al beschikbaar via
 * `/admin/communications` en `/admin/customers/{id}/communications`.
 */
function DebiteurenTijdlijnPlaceholder() {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-10 text-center" data-testid="debiteuren-tijdlijn-placeholder">
      <Clock className="w-8 h-8 mx-auto mb-3 text-slate-300" />
      <div className="text-sm font-semibold text-brand-900 mb-1">Debiteuren-tijdlijn</div>
      <div className="text-xs text-slate-500 max-w-md mx-auto">
        Chronologische feed van alle debiteuren-activiteit (herinneringen verzonden, betalingen ontvangen, Stripe-events, notities). Deze view komt in de volgende sprint. Voor nu: gebruik het tabblad <strong>Communicatie</strong> voor het volledige communicatielog per debiteur.
      </div>
    </div>
  );
}
